import { RiErrorWarningLine } from "@remixicon/react";
import { Alert } from "@screener/uikit/components/alert";
import { Button } from "@screener/uikit/components/button";
import { Field, FieldLabel } from "@screener/uikit/components/field";
import { Input } from "@screener/uikit/components/input";
import { type FormEvent, useCallback, useState } from "react";
import { AuthHeading, AuthLayout } from "@/auth/auth-layout";
import { ConfirmEmailScreen } from "@/auth/confirm-email-screen";
import { GoogleAlternative } from "@/auth/google-alternative";
import { type EmailUnconfirmed, type LoginResult, login as loginRequest } from "@/lib/auth-api";

/** Отказ входа, у которого есть текст сообщения (не успех и не экран подтверждения). */
export type LoginFailure = Exclude<LoginResult, "success" | EmailUnconfirmed>;

/** Верный пароль при неподтверждённой почте: вместо сообщения — экран «Подтвердите почту». */
export function isEmailUnconfirmed(result: LoginResult): result is EmailUnconfirmed {
  return typeof result === "object";
}

interface LoginFormProps {
  onSuccess: (login: string) => void;
  /**
   * Сообщение, не связанное с текущей попыткой входа — например, что
   * предыдущий выход не подтвердился демоном. Отдельно от `failureMessage`:
   * то относится к попытке входа, это — к тому, что привело человека на форму.
   */
  notice?: string | null;
  /**
   * Открытая регистрация включена (`signup-open`, §104 п. 8): только тогда на
   * форме есть ссылка «Зарегистрироваться». Выключена или не известна —
   * ссылки нет: она обещала бы несуществующее.
   */
  signupOpen?: boolean;
}

/**
 * Форма входа (§9, §6.6). Ссылка «Зарегистрироваться» — только при открытой
 * регистрации (`signupOpen`); иначе учётные записи заводит администратор или
 * приглашение (§104).
 */
export function LoginForm({ onSuccess, notice, signupOpen = false }: LoginFormProps) {
  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  const [pending, setPending] = useState(false);
  const [failure, setFailure] = useState<LoginFailure | null>(null);
  // Пароль в состояние экрана подтверждения не попадает: повтор письма идёт по логину.
  const [unconfirmed, setUnconfirmed] = useState<{ login: string; email: string } | null>(null);

  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (pending) {
        return;
      }
      const attemptedLogin = login;
      setPending(true);
      setFailure(null);
      loginRequest({ login: attemptedLogin, password })
        .then((result) => {
          setPassword("");
          if (result === "success") {
            onSuccess(attemptedLogin);
            return;
          }
          if (isEmailUnconfirmed(result)) {
            setUnconfirmed({ login: attemptedLogin, email: result.email });
            return;
          }
          setFailure(result);
        })
        .finally(() => {
          setPending(false);
        });
    },
    [login, password, pending, onSuccess],
  );

  const failureMessage = failure === null ? null : describeFailure(failure);

  if (unconfirmed !== null) {
    return (
      <AuthLayout>
        <ConfirmEmailScreen
          login={unconfirmed.login}
          email={unconfirmed.email}
          onBack={() => setUnconfirmed(null)}
        />
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <form onSubmit={handleSubmit} className="flex flex-col gap-6" aria-label="Вход">
        <AuthHeading title="Вход в kelv" />
        {notice ? (
          <p role="status" className="text-sm text-muted-foreground">
            {notice}
          </p>
        ) : null}
        {failureMessage ? (
          <Alert variant="error" size="sm">
            <RiErrorWarningLine aria-hidden="true" />
            <span>{failureMessage}</span>
          </Alert>
        ) : null}
        <div className="flex flex-col gap-3.5">
          <Field>
            <FieldLabel className="text-xs">Логин</FieldLabel>
            <Input
              size="lg"
              name="login"
              autoComplete="username"
              value={login}
              onChange={(event) => setLogin(event.target.value)}
              disabled={pending}
              required
            />
          </Field>
          <Field>
            <FieldLabel className="text-xs">Пароль</FieldLabel>
            <Input
              size="lg"
              name="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              disabled={pending}
              required
            />
          </Field>
        </div>
        <Button type="submit" size="lg" className="w-full" disabled={pending}>
          {pending ? "Вход…" : "Войти"}
        </Button>
        {/* Видна всегда, не зависит от почтового сервиса: ответ на запрос сброса прежний (§133 п. 6). */}
        <Button variant="link" size="sm" nativeButton={false} render={<a href="#/forgot" />}>
          Забыли пароль?
        </Button>
        <GoogleAlternative />
        {signupOpen ? (
          <Button variant="link" size="sm" nativeButton={false} render={<a href="#/signup" />}>
            Зарегистрироваться
          </Button>
        ) : null}
      </form>
    </AuthLayout>
  );
}

/**
 * Текст неудачи входа. `switch` вместо тернарной цепочки: пятый исход
 * `LoginResult` заставляет `satisfies never` не собраться, а не молча дать
 * человеку неудачу вообще без сообщения (design review stage5-auth-sessions,
 * пункт 5).
 */
export function describeFailure(failure: LoginFailure): string {
  switch (failure) {
    case "rate-limited":
      return "Слишком много попыток входа. Подождите и попробуйте снова.";
    case "unavailable":
      return "Демон не отвечает. Попробуйте позже.";
    case "invalid-credentials":
      return "Не удалось войти. Проверьте логин и пароль.";
    default:
      return failure satisfies never;
  }
}
